import React, { useState } from 'react';
import { useParticipants } from '../../hooks/useParticipants';
import { StudyParticipant, ParticipantStatus } from '../../types/study.types';

interface ParticipantTableProps {
  studyId: string;
  participants: StudyParticipant[];
  loading: boolean;
}

const ParticipantTable: React.FC<ParticipantTableProps> = ({
  studyId,
  participants,
  loading,
}) => {
  const { withdrawParticipant, markLostToFollowup, resendSurveyEmail } = useParticipants(studyId);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ParticipantStatus | 'all'>('all');
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [expandedParticipant, setExpandedParticipant] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const filteredParticipants = participants.filter((p) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      p.participantCode.toLowerCase().includes(searchLower) ||
      p.email?.toLowerCase().includes(searchLower);
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadgeColor = (status: ParticipantStatus) => {
    switch (status) {
      case 'enrolled':
        return 'bg-blue-100 text-blue-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-teal-100 text-teal-800';
      case 'withdrawn':
        return 'bg-red-100 text-red-800';
      case 'lost_to_followup':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getProgressBadgeColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'missed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleWithdraw = async (participantId: string, code: string) => {
    const reason = prompt(`Reason for withdrawing ${code}?`);
    if (reason === null) return;

    setLoadingAction(participantId);
    setActionError(null);
    try {
      await withdrawParticipant(participantId, reason || undefined);
    } catch (err: any) {
      setActionError(err.message || 'Failed to withdraw participant');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleMarkLost = async (participantId: string, code: string) => {
    if (!window.confirm(`Mark ${code} as lost to follow-up?`)) return;

    setLoadingAction(participantId);
    setActionError(null);
    try {
      await markLostToFollowup(participantId);
    } catch (err: any) {
      setActionError(err.message || 'Failed to update participant status');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleResendEmail = async (participantId: string, timepointId: string) => {
    if (!window.confirm('Resend survey email for this timepoint?')) return;

    setLoadingAction(`${participantId}-${timepointId}`);
    setActionError(null);
    try {
      await resendSurveyEmail(participantId, timepointId);
      alert('Email sent successfully');
    } catch (err: any) {
      setActionError(err.message || 'Failed to resend email');
    } finally {
      setLoadingAction(null);
    }
  };

  const getCompletionRate = (participant: StudyParticipant) => {
    if (!participant.surveyProgress) return 0;
    const progressEntries = Object.values(participant.surveyProgress);
    if (progressEntries.length === 0) return 0;
    const completed = progressEntries.filter((p) => p.status === 'completed').length;
    return Math.round((completed / progressEntries.length) * 100);
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-3 text-sm text-gray-600">Loading participants...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Error Display */}
      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {actionError}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Search by code or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ParticipantStatus | 'all')}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Statuses</option>
          <option value="enrolled">Enrolled</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="withdrawn">Withdrawn</option>
          <option value="lost_to_followup">Lost to Follow-up</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-xs text-gray-500 uppercase font-semibold">Total</p>
          <p className="text-xl font-bold text-gray-900">{participants.length}</p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-xs text-gray-500 uppercase font-semibold">Active</p>
          <p className="text-xl font-bold text-green-600">
            {participants.filter((p) => p.status === 'active' || p.status === 'enrolled').length}
          </p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-xs text-gray-500 uppercase font-semibold">Completed</p>
          <p className="text-xl font-bold text-teal-600">
            {participants.filter((p) => p.status === 'completed').length}
          </p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-xs text-gray-500 uppercase font-semibold">Withdrawn</p>
          <p className="text-xl font-bold text-red-600">
            {participants.filter((p) => p.status === 'withdrawn').length}
          </p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-xs text-gray-500 uppercase font-semibold">Lost</p>
          <p className="text-xl font-bold text-yellow-600">
            {participants.filter((p) => p.status === 'lost_to_followup').length}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {filteredParticipants.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all'
              ? 'No participants found matching your filters.'
              : 'No participants enrolled yet.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Enrolled
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredParticipants.map((participant) => (
                  <React.Fragment key={participant.id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() =>
                        setExpandedParticipant(
                          expandedParticipant === participant.id ? null : participant.id
                        )
                      }
                    >
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <div className="flex items-center">
                          <svg
                            className={`h-4 w-4 text-gray-400 mr-2 transition-transform ${
                              expandedParticipant === participant.id ? 'rotate-90' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                          <span className="text-sm font-mono font-medium text-gray-900">
                            {participant.participantCode}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusBadgeColor(
                            participant.status
                          )}`}
                        >
                          {participant.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-500">
                        {formatDate(participant.enrolledAt)}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-teal-600 h-2 rounded-full"
                              style={{ width: `${getCompletionRate(participant)}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-600">
                            {getCompletionRate(participant)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-right text-xs font-medium">
                        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          {(participant.status === 'enrolled' || participant.status === 'active') && (
                            <>
                              <button
                                onClick={() => handleMarkLost(participant.id, participant.participantCode)}
                                disabled={loadingAction === participant.id}
                                className="text-yellow-600 hover:text-yellow-900 disabled:opacity-50"
                              >
                                Mark Lost
                              </button>
                              <button
                                onClick={() => handleWithdraw(participant.id, participant.participantCode)}
                                disabled={loadingAction === participant.id}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              >
                                Withdraw
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded Details */}
                    {expandedParticipant === participant.id && (
                      <tr>
                        <td colSpan={5} className="px-4 py-4 bg-gray-50">
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">Email:</span>
                                <span className="ml-2 text-gray-900">{participant.email || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Consent Date:</span>
                                <span className="ml-2 text-gray-900">
                                  {formatDate(participant.consentAcknowledgedAt)}
                                </span>
                              </div>
                              {participant.withdrawnAt && (
                                <div>
                                  <span className="text-gray-500">Withdrawn:</span>
                                  <span className="ml-2 text-gray-900">
                                    {formatDate(participant.withdrawnAt)}
                                  </span>
                                </div>
                              )}
                              {participant.withdrawalReason && (
                                <div>
                                  <span className="text-gray-500">Reason:</span>
                                  <span className="ml-2 text-gray-900">
                                    {participant.withdrawalReason}
                                  </span>
                                </div>
                              )}
                            </div>
                            {/* Survey Progress */}
                            {participant.surveyProgress && Object.keys(participant.surveyProgress).length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-2">
                                  Survey Progress
                                </h4>
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600">
                                          Timepoint
                                        </th>
                                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600">
                                          Status
                                        </th>
                                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600">
                                          Due
                                        </th>
                                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600">
                                          Completed
                                        </th>
                                        <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-600">
                                          Actions
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {Object.entries(participant.surveyProgress).map(([timepointId, progress]) => (
                                        <tr key={timepointId}>
                                          <td className="px-3 py-2">{timepointId}</td>
                                          <td className="px-3 py-2">
                                            <span
                                              className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getProgressBadgeColor(
                                                progress.status
                                              )}`}
                                            >
                                              {progress.status}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2 text-gray-500">
                                            {formatDate(progress.dueDate)}
                                          </td>
                                          <td className="px-3 py-2 text-gray-500">
                                            {progress.completedAt
                                              ? formatDate(progress.completedAt)
                                              : '-'}
                                          </td>
                                          <td className="px-3 py-2 text-right">
                                            {progress.status !== 'completed' && (
                                              <button
                                                onClick={() =>
                                                  handleResendEmail(participant.id, timepointId)
                                                }
                                                disabled={
                                                  loadingAction ===
                                                  `${participant.id}-${timepointId}`
                                                }
                                                className="text-blue-600 hover:text-blue-900 text-xs disabled:opacity-50"
                                              >
                                                Resend Email
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParticipantTable;
